import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ProfileReference} from '@github-ui/profile-reference'
import {BlockedIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {UserBlockedEvent$key} from './__generated__/UserBlockedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import styles from './UserBlockedEvent.module.css'

type UserBlockedEventProps = {
  queryRef: UserBlockedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function UserBlockedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
}: UserBlockedEventProps): React.ReactElement {
  const {actor, createdAt, blockedUser, blockDuration, databaseId} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment UserBlockedEvent on UserBlockedEvent {
        databaseId
        createdAt
        blockDuration
        actor {
          ...TimelineRowEventActor
        }
        blockedUser: subject {
          login
          name
        }
      }
    `,
    queryRef,
  )
  const highlighted = String(databaseId) === highlightedEventId
  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={BlockedIcon}
    >
      <TimelineRow.Main>
        <span>{`${blockDuration !== 'PERMANENT' ? `${LABELS.timeline.temporarily} ` : ''} ${
          LABELS.timeline.blocked
        } `}</span>
        <Link href={`/${blockedUser?.login}`} inline className={styles.Link}>
          {isFeatureEnabled('render_user_display_name') ? (
            <ProfileReference login={blockedUser?.login ?? ''} profileName={blockedUser?.name} isAgent={false} />
          ) : (
            blockedUser?.login
          )}
        </Link>
      </TimelineRow.Main>
    </TimelineRow>
  )
}

try{ UserBlockedEvent.displayName ||= 'UserBlockedEvent' } catch {}